/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react"
import { makeAutoObservable } from "mobx"
import { t } from "@lingui/macro"

import { FormFields } from "src/lib/form-fields"
import { chatbot_UpdateFeedbackRequest } from "src/api/models/chatbot_UpdateFeedbackRequest"
import { ChatbotAdminService } from "src/api/services/ChatbotAdminService"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { createLoadingKeys } from "src/lib/loading"
import { IChatbotFormFields } from "src/modals/chatbot/types/formFields"
import { chatbot_Tags } from "src/api"
import { getTagIds } from "src/modals/chatbot/helpers/getTagIdsFromTag"
import { reportError, reportUnhandledApiError } from "src/lib/report"
import {
    EMessageSender,
    IChatMessage,
} from "src/types/chat-message/chatMessage"

export class ChatbotDetailStore {
    static Context = React.createContext<ChatbotDetailStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "loading")

    messages: IChatMessage[] = []
    formFields = new FormFields<IChatbotFormFields>({})
    isTranslationEnabled = false
    isPublicConversation: boolean = false
    propertyObjectSegmentId?: number

    currentConversationId: string = ""
    allConversationIds: string[] = []
    currentIndex: number = -1

    constructor() {
        makeAutoObservable(this)
    }

    setNavigationData(conversationIds: string[], currentId: string) {
        this.allConversationIds = conversationIds
        this.currentConversationId = currentId
        this.currentIndex = this.allConversationIds.findIndex(
            (id) => id === currentId,
        )
    }

    goNext(): boolean {
        return this.currentIndex < this.allConversationIds.length - 1
    }

    goPrevious(): boolean {
        return this.currentIndex > 0
    }

    @loads(() => ChatbotDetailStore.LoadingKeys.loading)
    async navigateToConversation(direction: "next" | "previous") {
        if (direction === "next" && !this.goNext()) return
        if (direction === "previous" && !this.goPrevious()) return

        const newIndex =
            direction === "next" ? this.currentIndex + 1 : this.currentIndex - 1
        const newConversationId = this.allConversationIds[newIndex]

        if (newConversationId.length > 0) {
            this.currentIndex = newIndex
            this.currentConversationId = newConversationId
            await this.init(newConversationId, this.isPublicConversation)
        }
    }

    //#region setters
    setMessages(messages: IChatMessage[]) {
        this.messages = messages
    }
    setIsTranslationEnabled(isTranslationEnabled: boolean) {
        this.isTranslationEnabled = isTranslationEnabled
    }
    //#endregion

    //#region store operations
    @loads(() => ChatbotDetailStore.LoadingKeys.init)
    async init(conversationId: string, isPublicConversation: boolean = false) {
        this.isPublicConversation = isPublicConversation
        await this.loadConversationDetails(conversationId)
        await this.loadMessages(conversationId)
    }

    loadMessages = async (conversationId: string) => {
        try {
            const messagesResponse = this.isPublicConversation
                ? await ChatbotAdminService.getV1AdminChatbotPublicConversationsMessages(
                      {
                          conversationId,
                      },
                  )
                : await ChatbotAdminService.getV1AdminChatbotConversationsMessages(
                      {
                          conversationId,
                      },
                  )

            const newMessagesFormat = messagesResponse.map(
                (message): IChatMessage => {
                    const sentByUser = message?.role === "HUMAN"

                    return {
                        authorType: message?.role ?? "",
                        authorName: sentByUser
                            ? t`chatbot-view.modal.chat.tenant`
                            : t`chatbot-view.modal.chat.chatbot`,
                        createdAt: message?.created_at ?? "",
                        messageId: message?.message_id ?? "",
                        text: message?.message ?? "",
                        source: message?.sources ?? [],
                        messageSender: sentByUser
                            ? EMessageSender.User
                            : EMessageSender.Chatbot,
                        messageType: message?.message_type ?? undefined,
                        nodeInfo: message?.node_info ?? undefined,
                        messageDurationMs:
                            message?.message_duration_ms ?? undefined,
                        sourceEval: message?.source_eval ?? undefined,
                        hidden: message?.hidden ?? undefined,
                    }
                },
            )

            this.setMessages(newMessagesFormat ?? [])
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }

    loadConversationDetails = async (conversationId: string) => {
        try {
            if (this.isPublicConversation) {
                const response =
                    await ChatbotAdminService.getV1AdminChatbotPublicConversations(
                        {
                            conversationId,
                        },
                    )
                this.propertyObjectSegmentId = undefined
                this.formFields.init({
                    conversation_id: response.conversation_id ?? "",
                    tenant_id: 0,
                    tenant_feedback_reason:
                        response.user_feedback?.reason ?? "",
                    tenant_feedback_thumb_status:
                        response.user_feedback?.thumb_status ?? -1,
                    admin_feedback_reason:
                        response.admin_feedback?.reason ?? "",
                    admin_feedback_thumb_status:
                        response?.admin_feedback?.thumb_status ?? undefined,
                    impersonated_by: undefined,
                    tags: response.tags,
                    created_at:
                        response.created_at !== undefined
                            ? new Date(response.created_at)
                            : undefined,
                    resolution: response.resolution ?? "",
                    ended_at: response.ended_at,
                })
            } else {
                const response =
                    await ChatbotAdminService.getV1AdminChatbotConversations({
                        conversationId,
                    })
                this.propertyObjectSegmentId =
                    response.property_object_segment_id
                this.formFields.init({
                    conversation_id: response.conversation_id ?? "",
                    tenant_id: response.tenant_id ?? 0,
                    tenant_feedback_reason:
                        response.tenant_feedback?.reason ?? "",
                    tenant_feedback_thumb_status:
                        response.tenant_feedback?.thumb_status ?? -1,
                    admin_feedback_reason:
                        response.admin_feedback?.reason ?? "",
                    admin_feedback_thumb_status:
                        response?.admin_feedback?.thumb_status ?? undefined,
                    impersonated_by: response.impersonated_by,
                    tags: response.tags,
                    created_at:
                        response.created_at !== undefined
                            ? new Date(response.created_at)
                            : undefined,
                    resolution: response.resolution ?? "",
                    ended_at: response.ended_at,
                })
            }
        } catch (error) {
            reportError(
                t`chatbot-view.modal.error.conversations-messages-load`,
                error,
            )
        }
    }

    loadTranslationMessages = async (conversationId: string, lang: string) => {
        try {
            this.setIsTranslationEnabled(!this.isTranslationEnabled)
            const translationResponse = this.isTranslationEnabled
                ? await ChatbotAdminService.getV1AdminChatbotConversationsMessagesTranslate(
                      {
                          conversationId,
                          lang,
                      },
                  )
                : { messages: [] }

            const messages = [...this.messages]

            messages.map((message) => {
                return (message.translationText =
                    translationResponse?.messages?.find(
                        (translation: any) =>
                            translation.message_id === message?.messageId,
                    )?.message ?? "")
            })

            this.setMessages([...messages])
        } catch (error) {
            reportUnhandledApiError(error)
        }
    }

    @loads(() => ChatbotDetailStore.LoadingKeys.loading)
    async updateFeedback(
        request: chatbot_UpdateFeedbackRequest,
        conversationId: string,
    ) {
        try {
            if (this.isPublicConversation) {
                // Feedback updates not supported for public conversations yet
                return
            }
            await ChatbotAdminService.postV1AdminChatbotConversationsUpdateFeedback(
                { request, conversationId },
            )
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "chatbotConversation",
                    action: "update",
                },
            })
        } catch (error) {
            reportError(
                t`chatbot-view.modal.error.update-admin-feedback`,
                error,
            )
        }
    }

    @loads(() => ChatbotDetailStore.LoadingKeys.loading)
    async updateTag(tag_ids: chatbot_Tags, conversationId: string) {
        try {
            if (this.isPublicConversation) {
                // Tag updates not supported for public conversations yet
                return
            }
            await ChatbotAdminService.putV1AdminChatbotConversationsTag({
                conversationId,
                request: {
                    tag_ids: getTagIds(tag_ids),
                },
            })
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "chatbotConversation",
                    action: "update",
                },
            })
        } catch (error) {
            reportError(t`chatbot-view.modal.error.update-tags`, error)
        }
    }
    //#endregion

    //#region helpers
    @loads(() => ChatbotDetailStore.LoadingKeys.loading)
    async handleSubmit(conversationId: string) {
        const adminFeedbackThumbStatus = this.formFields.get(
            "admin_feedback_thumb_status",
        )
        const request: chatbot_UpdateFeedbackRequest = {
            reason: this.formFields.get("admin_feedback_reason"),
            thumb_status:
                adminFeedbackThumbStatus === undefined
                    ? undefined
                    : (adminFeedbackThumbStatus as chatbot_UpdateFeedbackRequest["thumb_status"]),
        }

        await this.updateFeedback(request, conversationId)
        await this.handleTagsSubmit(conversationId)
    }

    async handleTagsSubmit(conversationId: string) {
        const tags = this.formFields.get("tags")
        const shouldUpdateTags = this.formFields.getIsFieldDirty("tags")

        if (tags !== undefined && shouldUpdateTags) {
            await this.updateTag(tags, conversationId)
        }
    }
    //#endregion
}
